import * as React from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Check, CaretRight } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { useOverlayContainer } from './overlay'

/**
 * DropdownMenu — an action menu (Radix-backed), from the Figma DS2026 "Dropdown"
 * specs (First Bite Design System, nodes 7209 / 7215 / 7216). Trigger anything
 * (a button, a kebab icon, an avatar) and list *actions* — not form values; for
 * a value picker use `Select` (Phase 4).
 *
 *   <DropdownMenu>
 *     <DropdownMenuTrigger asChild><Button>Open</Button></DropdownMenuTrigger>
 *     <DropdownMenuContent>
 *       <DropdownMenuItem icon={<User />}>Profile</DropdownMenuItem>
 *       <DropdownMenuItem shortcut="⌘K">Shortcuts</DropdownMenuItem>
 *       <DropdownMenuSeparator />
 *       <DropdownMenuItem>Sign out</DropdownMenuItem>
 *     </DropdownMenuContent>
 *   </DropdownMenu>
 *
 * Tokens (Figma → repo): white panel, gray-200 border, `shadow-fbui-lg`; items
 * are 14px gray-700 with a gray-100 highlight fill; leading icons gray-500;
 * trailing ⌘K shortcut hints 12px tertiary gray. Radix handles focus, typeahead,
 * keyboard nav, and focus return for free.
 *
 * Item variants (from the menubar Figma, node 16531:29966):
 *  - `DropdownMenuCheckboxItem` / `DropdownMenuRadioItem` — leading ✓ indicator
 *  - `DropdownMenuItem destructive` — red "Delete"-style action
 *  - `DropdownMenuItem description=` — two-line item (label + subtitle)
 *  - `DropdownMenuItem inset` / `DropdownMenuLabel inset` — indent an icon-less
 *    row so its label lines up with sibling rows that have a leading icon
 *  - `DropdownMenuSub` / `SubTrigger` (trailing caret) / `SubContent` — submenus
 *
 * `DropdownMenuContent size="sm"` compacts every item (rows/text/glyphs) to pair
 * with a small trigger button; the size flows to items (and submenus) via context.
 * A long menu caps at the collision-available height and scrolls automatically.
 */

export const DropdownMenu = DropdownMenuPrimitive.Root
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger
export const DropdownMenuGroup = DropdownMenuPrimitive.Group
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal

export interface DropdownMenuContentProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Content
> {
  /** Compact menu (smaller rows/text) to pair with a small trigger. */
  size?: DropdownMenuSize
}

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(({ className, sideOffset = 6, size = 'md', children, ...props }, ref) => {
  // Portal into the enclosing Sheet/Dialog when nested in one, so the menu isn't
  // closed by the modal's focus trap / outside-interaction (see PopoverContent).
  const container = useOverlayContainer()
  return (
    <DropdownMenuPrimitive.Portal container={container ?? undefined}>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          // A long menu caps at the available height to the collision edge and
          // scrolls (Radix sets the var), so it never runs off the viewport.
          'fbui-scroll z-50 min-w-48 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-1.5 shadow-fbui-lg',
          'origin-[--radix-dropdown-menu-content-transform-origin]',
          className
        )}
        {...props}
      >
        <DropdownMenuSizeContext.Provider value={size}>
          {children}
        </DropdownMenuSizeContext.Provider>
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  )
})
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

type DropdownMenuSize = 'sm' | 'md'

/** Published by `DropdownMenuContent` so every item type matches the menu size. */
const DropdownMenuSizeContext = React.createContext<DropdownMenuSize>('md')
const useMenuSize = () => React.useContext(DropdownMenuSizeContext)

/** Shared row layout + highlight/disabled states for every menu item type. */
const itemBase =
  'group flex cursor-pointer items-center rounded-lg text-fbui-gray-700 outline-none select-none data-[highlighted]:bg-fbui-gray-100 data-[highlighted]:text-fbui-gray-900 data-[disabled]:pointer-events-none data-[disabled]:text-fbui-gray-300'

/** Per-size row spacing + text. */
const itemSize: Record<DropdownMenuSize, string> = {
  sm: 'gap-2 px-2.5 py-1.5 text-xs',
  md: 'gap-2.5 px-[0.75rem] py-2 text-sm',
}

/** Per-size icon / indicator glyph size. */
const glyphSize: Record<DropdownMenuSize, string> = {
  sm: 'size-3.5',
  md: 'size-4',
}

/**
 * Empty leading slot that reserves the icon's width so an icon-less row's label
 * lines up with sibling rows that have a leading icon (the `inset` treatment).
 */
const InsetSpacer = ({ size }: { size: DropdownMenuSize }) => (
  <span aria-hidden className={cn('shrink-0', glyphSize[size])} />
)

/** Destructive override — red text + red-50 highlight (the Figma "Delete"). */
const itemDestructive =
  'text-fbui-red-700 data-[highlighted]:bg-fbui-red-50 data-[highlighted]:text-fbui-red-700'

/** Trailing keyboard-shortcut hint. */
export const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn('ml-auto pl-[1rem] text-xs text-fbui-gray-400', className)}
    {...props}
  />
)
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

export interface DropdownMenuItemProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Item
> {
  /** Leading icon (sized to 16px, gray). */
  icon?: React.ReactNode
  /** Trailing keyboard-shortcut hint (e.g. "⌘K"). */
  shortcut?: React.ReactNode
  /** Secondary subtitle line below the label (two-line item). */
  description?: React.ReactNode
  /** Red destructive treatment (e.g. "Delete"). */
  destructive?: boolean
  /**
   * Indent an icon-less item so its label lines up with sibling rows that *do*
   * have a leading icon. No-op when `icon` is set (the icon already fills the
   * slot). Keeps mixed menus from reading ragged.
   */
  inset?: boolean
}

export const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(
  (
    {
      className,
      icon,
      shortcut,
      description,
      destructive,
      inset,
      children,
      ...props
    },
    ref
  ) => {
    const size = useMenuSize()
    return (
      <DropdownMenuPrimitive.Item
        ref={ref}
        className={cn(
          itemBase,
          itemSize[size],
          '[&>svg]:shrink-0',
          size === 'sm' ? '[&>svg]:size-3.5' : '[&>svg]:size-4',
          // Top-align the icon with the first line when there's a subtitle.
          description && 'items-start',
          destructive && itemDestructive,
          className
        )}
        {...props}
      >
        {icon ? (
          <span
            className={cn(
              'flex shrink-0 items-center justify-center group-data-[disabled]:text-fbui-gray-300',
              glyphSize[size],
              size === 'sm' ? '[&>svg]:size-3.5' : '[&>svg]:size-4',
              description && 'mt-0.5',
              destructive ? 'text-fbui-red-700' : 'text-fbui-gray-500'
            )}
          >
            {icon}
          </span>
        ) : inset ? (
          <InsetSpacer size={size} />
        ) : null}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">{children}</span>
          {description && (
            <span
              className={cn(
                'truncate text-xs font-normal group-data-[disabled]:text-fbui-gray-300',
                destructive
                  ? 'text-fbui-red-500'
                  : 'text-fbui-gray-500 group-data-[highlighted]:text-fbui-gray-600'
              )}
            >
              {description}
            </span>
          )}
        </span>
        {shortcut && <DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut>}
      </DropdownMenuPrimitive.Item>
    )
  }
)
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

export interface DropdownMenuCheckboxItemProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.CheckboxItem
> {
  /** Trailing keyboard-shortcut hint. */
  shortcut?: React.ReactNode
  /**
   * Close the menu when toggled. Defaults to `false` so the menu stays open and
   * you can see the result of the toggle (and flip several in a row).
   */
  closeOnSelect?: boolean
}

/** Toggleable item with a leading ✓ when checked (the Figma checkmark variant). */
export const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  DropdownMenuCheckboxItemProps
>(
  (
    {
      className,
      shortcut,
      children,
      onSelect,
      closeOnSelect = false,
      ...props
    },
    ref
  ) => {
    const size = useMenuSize()
    return (
      <DropdownMenuPrimitive.CheckboxItem
        ref={ref}
        className={cn(itemBase, itemSize[size], className)}
        onSelect={(event) => {
          onSelect?.(event)
          if (!closeOnSelect) event.preventDefault()
        }}
        {...props}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center',
            glyphSize[size]
          )}
        >
          <DropdownMenuPrimitive.ItemIndicator>
            <Check
              weight="bold"
              className={glyphSize[size]}
              aria-hidden="true"
            />
          </DropdownMenuPrimitive.ItemIndicator>
        </span>
        <span className="flex-1 truncate">{children}</span>
        {shortcut && <DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut>}
      </DropdownMenuPrimitive.CheckboxItem>
    )
  }
)
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName

export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

export interface DropdownMenuRadioItemProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.RadioItem
> {
  /** Trailing keyboard-shortcut hint. */
  shortcut?: React.ReactNode
  /**
   * Close the menu when selected. Defaults to `false` so the menu stays open and
   * you can see which option is now selected.
   */
  closeOnSelect?: boolean
}

/** Single-choice item (inside a RadioGroup) with a leading ✓ when selected. */
export const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  DropdownMenuRadioItemProps
>(
  (
    {
      className,
      shortcut,
      children,
      onSelect,
      closeOnSelect = false,
      ...props
    },
    ref
  ) => {
    const size = useMenuSize()
    return (
      <DropdownMenuPrimitive.RadioItem
        ref={ref}
        className={cn(itemBase, itemSize[size], className)}
        onSelect={(event) => {
          onSelect?.(event)
          if (!closeOnSelect) event.preventDefault()
        }}
        {...props}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center',
            glyphSize[size]
          )}
        >
          <DropdownMenuPrimitive.ItemIndicator>
            <Check
              weight="bold"
              className={glyphSize[size]}
              aria-hidden="true"
            />
          </DropdownMenuPrimitive.ItemIndicator>
        </span>
        <span className="flex-1 truncate">{children}</span>
        {shortcut && <DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut>}
      </DropdownMenuPrimitive.RadioItem>
    )
  }
)
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

export interface DropdownMenuLabelProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Label
> {
  /** Indent to align with items that have a leading icon. */
  inset?: boolean
}

export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  DropdownMenuLabelProps
>(({ className, inset, children, ...props }, ref) => {
  const size = useMenuSize()
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn(
        'flex items-center px-[0.75rem] py-1.5 text-xs font-medium text-fbui-gray-500',
        size === 'sm' ? 'gap-2' : 'gap-2.5',
        className
      )}
      {...props}
    >
      {inset && <InsetSpacer size={size} />}
      <span className="truncate">{children}</span>
    </DropdownMenuPrimitive.Label>
  )
})
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-1.5 my-1.5 h-px bg-fbui-gray-200', className)}
    {...props}
  />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

export const DropdownMenuSub = DropdownMenuPrimitive.Sub

export interface DropdownMenuSubTriggerProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.SubTrigger
> {
  /** Leading icon (sized to 16px, gray). */
  icon?: React.ReactNode
  /** Indent an icon-less submenu trigger to align with icon'd sibling rows. */
  inset?: boolean
}

/** Item that opens a submenu — highlights while open, trailing caret. */
export const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  DropdownMenuSubTriggerProps
>(({ className, icon, inset, children, ...props }, ref) => {
  const size = useMenuSize()
  return (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      className={cn(
        itemBase,
        itemSize[size],
        'data-[state=open]:bg-fbui-gray-100',
        className
      )}
      {...props}
    >
      {icon ? (
        <span
          className={cn(
            'flex shrink-0 items-center justify-center text-fbui-gray-500 group-data-[disabled]:text-fbui-gray-300',
            glyphSize[size],
            size === 'sm' ? '[&>svg]:size-3.5' : '[&>svg]:size-4'
          )}
        >
          {icon}
        </span>
      ) : inset ? (
        <InsetSpacer size={size} />
      ) : null}
      <span className="flex-1 truncate">{children}</span>
      <CaretRight
        weight="bold"
        aria-hidden="true"
        className="ml-auto size-3.5 shrink-0 text-fbui-gray-500"
      />
    </DropdownMenuPrimitive.SubTrigger>
  )
})
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName

export const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => {
  const container = useOverlayContainer()
  return (
    <DropdownMenuPrimitive.Portal container={container ?? undefined}>
      <DropdownMenuPrimitive.SubContent
        ref={ref}
        className={cn(
          'fbui-scroll z-50 min-w-44 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-1.5 shadow-fbui-lg',
          'origin-[--radix-dropdown-menu-content-transform-origin]',
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
})
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName

export interface DropdownMenuProfileHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Avatar node (e.g. <Avatar />). */
  avatar?: React.ReactNode
  name: React.ReactNode
  email?: React.ReactNode
}

/**
 * Non-interactive header block (avatar · name · email) for the profile-menu
 * variant in the Figma. Pair with a `DropdownMenuSeparator` below it.
 */
export const DropdownMenuProfileHeader = React.forwardRef<
  HTMLDivElement,
  DropdownMenuProfileHeaderProps
>(({ className, avatar, name, email, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center gap-2.5 px-[0.75rem] py-2', className)}
    {...props}
  >
    {avatar}
    <div className="min-w-0">
      <div className="truncate text-sm font-medium text-fbui-gray-800">
        {name}
      </div>
      {email && (
        <div className="truncate text-xs text-fbui-gray-500">{email}</div>
      )}
    </div>
  </div>
))
DropdownMenuProfileHeader.displayName = 'DropdownMenuProfileHeader'
